import React, { useState, useEffect, useRef } from 'react';
import { Player } from '@lordicon/react';
import styles from './Timeline.module.css';
import Button from '../button/Button';

import organizationIcon from "../../icons/wired-outline-140-earphone-telephone-hover-calling.json"
import growthIcon from "../../icons/wired-outline-1854-office-desk-hover-pinch.json"
import focusIcon from "../../icons/wired-outline-747-keyboard-hover-pinch.json"
import allforus from "../../icons/wired-outline-1846-employee-working-hover-working (1).json"

export default function ElegantTimeline() {
  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const timelineRef = useRef(null);
  const cardsRef = useRef([]);
  const playerRefs = useRef([]);

  const items = [
    {
      icon: organizationIcon,
      title: "אתה לא צריך לעקוב אחרי כל פרט קטן",
      description:
        "הטלפון מצלצל? מישהו עונה. מייל נכנס? מישהו עוקב. פגישה מתקרבת? התזכורת כבר יצאה. אין צורך לדאוג שמשהו ייפול בין הכיסאות.",
    },
    {
      icon: growthIcon,
      title: "אתה פרודוקטיבי לאורך כל היום",
      description:
        "תגיד ביי לשעות על אקסלים ולכל הדברים ששואבים לך את הזמן ושוחקים אותך. הראש שלך פנוי לחשוב, לפתח מוצרים חדשים, לסגור עסקאות גדולות יותר.",
    },
    {
      icon: focusIcon,
      title: "כל ליד נרשם ומטופל כמו שצריך",
      description:
        "מישהו פנה אליך? הוא מקבל תגובה תוך שעות ולא ימים. יש לו שאלות נוספות? מישהו כבר עוקב. ההתלהבות שלו לא מתקררת כי לקח לך זמן לחזור.",
    },
    {
      icon: allforus,
      title: "כל מה שאתה לא רוצה להתעסק בו - עובר אלינו",
      description:
        "רוצה לסגור מסעדה לערב? כרטיסי טיסה לחופשה? למלא טפסים? אנחנו נשארים בשבילך גם בדברים הקטנים, כדי שאתה תישאר ממוקד בעיקר.",
    },
  ];

  // Smooth scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const element = timelineRef.current;
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const startPoint = windowHeight * 0.75;
      const endPoint = windowHeight * 0.25;

      if (rect.top > startPoint) {
        setProgress(0);
        setActiveIndex(-1);
      } else if (rect.top + rect.height < endPoint) {
        setProgress(100);
        setActiveIndex(items.length - 1);
      } else {
        const scrolled = startPoint - rect.top;
        const total = rect.height + (startPoint - endPoint);
        const percentage = (scrolled / total) * 100;
        setProgress(Math.min(Math.max(percentage, 0), 100));
        
        // Calculate active index
        const newActiveIndex = Math.floor((percentage / 100) * items.length);
        setActiveIndex(Math.min(newActiveIndex, items.length - 1));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [items.length]);

  // Mouse tracking for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleComplete = (index) => {
    setTimeout(() => {
      playerRefs.current[index]?.playFromBeginning();
    }, 3000);
  };

  useEffect(() => {
    playerRefs.current.forEach((ref) => {
      ref?.playFromBeginning();
    });
  }, []);

  return (
    <div className={styles.container}>
      {/* Floating decorative elements */}
      <div 
        className={styles.floatingOrb1}
        style={{ transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)` }}
      />
      <div 
        className={styles.floatingOrb2}
        style={{ transform: `translate(${mousePosition.x * -0.3}px, ${mousePosition.y * -0.3}px)` }}
      />
      <div 
        className={styles.floatingOrb3}
        style={{ transform: `translate(${mousePosition.x * 0.4}px, ${mousePosition.y * -0.4}px)` }}
      />

      <div className={styles.wrapper}>
        {/* Header section */}
     

        <div ref={timelineRef} className={styles.timeline}>
          
          {/* Central flowing line */}
          <div className={styles.centralLine}>
            <svg className={styles.lineSvg} viewBox="0 0 4 100" preserveAspectRatio="none">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#8d6e63" stopOpacity="0.2" />
                  <stop offset="50%" stopColor="#6d4c41" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#5d4037" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#8d6e63" />
                  <stop offset="50%" stopColor="#6d4c41" />
                  <stop offset="100%" stopColor="#5d4037" />
                </linearGradient>
              </defs>
              <rect x="0" y="0" width="4" height="100" fill="url(#lineGradient)" rx="2" />
              <rect 
                x="0" 
                y="0" 
                width="4" 
                height={progress} 
                fill="url(#progressGradient)" 
                rx="2"
                className={styles.progressRect}
              />
            </svg>
            
            {/* Glowing dot at progress point */}
            <div 
              className={styles.progressDot}
              style={{ top: `${progress}%` }}
            >
              <div className={styles.dotCore} />
              <div className={styles.dotRing1} />
              <div className={styles.dotRing2} />
            </div>
          </div>

          {/* Timeline items */}
          <div className={styles.itemsContainer}>
            {items.map((item, index) => {
              const isActive = index <= activeIndex;
              const isCurrent = index === activeIndex;
              const itemProgress = Math.min(Math.max((progress - index * 25) * 4, 0), 100);

              return (
                <div 
                  key={index} 
                  ref={(el) => (cardsRef.current[index] = el)}
                  className={`${styles.item} ${isActive ? styles.itemActive : ''} ${isCurrent ? styles.itemCurrent : ''}`}
                >
                  {/* Milestone marker */}
                  <div className={styles.milestone}>
                    <div className={`${styles.milestoneOuter} ${isActive ? styles.milestoneOuterActive : ''}`}>
                      <div className={`${styles.milestoneInner} ${isActive ? styles.milestoneInnerActive : ''}`}>
                        <div className={styles.iconWrapper}>
                          <Player
                            icon={item.icon}
                            ref={(el) => (playerRefs.current[index] = el)}
                            size="100%"
                            colorize={isActive ? "#5d4037" : "#a1887f"}
                            onComplete={() => handleComplete(index)}
                          />
                        </div>
                      </div>
                      {isCurrent && (
                        <>
                          <div className={styles.milestonePulse1} />
                          <div className={styles.milestonePulse2} />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Content card */}
                  <div 
                    className={`${styles.card} ${isActive ? styles.cardActive : ''}`}
                    style={{
                      transform: `
                        translateX(${isActive ? 0 : 30}px) 
                        scale(${isCurrent ? 1.02 : 1})
                      `,
                      opacity: isActive ? 1 : 0.4,
                    }}
                  >
                    <div className={styles.cardShine} />
                    <div className={styles.cardContent}>
                      <h3 className={styles.cardTitle}>{item.title}</h3>
                      <div className={styles.divider}>
                        <div className={`${styles.dividerLine} ${isActive ? styles.dividerLineActive : ''}`} />
                        <div className={styles.dividerDot} />
                        <div className={`${styles.dividerLine} ${isActive ? styles.dividerLineActive : ''}`} />
                      </div>
                      <p className={styles.cardDescription}>{item.description}</p>
                    </div>
                    
                    {/* Decorative corner accents */}
                    <div className={`${styles.cornerAccent} ${styles.cornerTR}`} />
                    <div className={`${styles.cornerAccent} ${styles.cornerBL}`} />
                  </div>

                  {/* Connection line to card */}
                  <div className={`${styles.connectionLine} ${isActive ? styles.connectionLineActive : ''}`}>
                    <svg viewBox="0 0 60 2" className={styles.connectionSvg}>
                      <line 
                        x1="0" y1="1" x2="60" y2="1" 
                        stroke={isActive ? "#8d6e63" : "#d7ccc8"}
                        strokeWidth="2"
                        strokeDasharray={isActive ? "0" : "4 4"}
                        className={styles.connectionPath}
                      />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.buttonWrapper}>
          <Button text="כן, אני רוצה לשמוע עוד פרטים!" />
        </div>
      </div>
    </div>
  );
}